import React, { useEffect } from 'react';

import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';

import {
  useGetStaticPageDetailQuery,
  useUpdateStaticPageMutation,
} from './staticPagesApi';

import TextEditor from '../../../ui-components/TextEditor';
import { usePageHeaderContext } from '../../../context/PageHeaderContext';

interface FormValues {
  title: string;
  slug: string;
  content: string;
}

const StaticPageForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setTitle, setSubtitle, setBreadcrumbs, setActions } =
    usePageHeaderContext();

  const { data: pageData, isFetching } = useGetStaticPageDetailQuery(id ?? '', {
    skip: !id,
  });

  const [updatePage, { isLoading: isUpdating }] = useUpdateStaticPageMutation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      slug: '',
      content: '',
    },
  });

  useEffect(() => {
    if (pageData?.data) {
      const { pageName, slug, content } = pageData.data;
      reset({
        title: pageName ?? '',
        slug: slug ?? '',
        content: content ?? '',
      });
    }
  }, [pageData, reset]);

  useEffect(() => {
    setTitle('Edit Static Page');
    const pageName = pageData?.data?.pageName || '';
    setSubtitle(pageName ? `Editing: ${pageName}` : 'Editing Static Page');
    setBreadcrumbs([
      { label: 'Static Pages', to: '/configurations/static-pages' },
      { label: pageName || 'Edit', current: true },
    ]);

    return () => {
      setTitle(undefined);
      setSubtitle(undefined);
      setBreadcrumbs(undefined);
      setActions(undefined);
    };
  }, [setTitle, setSubtitle, setBreadcrumbs, setActions, pageData]);

  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    if (!id) return;
    try {
      await updatePage({
        pageId: id,
        title: formData.title,
        content: formData.content,
      }).unwrap();
      navigate('/configurations/static-pages');
    } catch (err: any) {
      // Error is handled globally in baseQuery
    }
  };

  const page = pageData?.data;

  if (isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-sm overflow-hidden">
          {/* Card Header */}
          <div className="flex items-start gap-3 px-6 pt-6 pb-5 border-b border-neutral-200 dark:border-neutral-800">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-brand-50 dark:bg-brand-900/20 text-brand-600 border border-brand-100 dark:border-brand-900/30">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-neutral-900 dark:text-white">
                {page?.pageName ?? 'Static Page'}
              </h2>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 font-medium">
                Last updated:{' '}
                {page?.lastUpdated
                  ? `${new Date(page.lastUpdated).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false,
                    })} UTC`
                  : 'N/A'}{' '}
                by {page?.updatedBy || 'System'}
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Page Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-neutral-600 dark:text-neutral-400 uppercase tracking-wider block">
                  Page Name <span className="text-error-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="title"
                  rules={{ required: 'Title is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter title"
                        className={`w-full px-3 py-2 bg-white dark:bg-neutral-900 border ${
                          error
                            ? 'border-error-500 focus:ring-error-500/10'
                            : 'border-neutral-200 dark:border-neutral-700 focus:ring-brand-500/10'
                        } rounded-lg text-sm text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-4 transition-all`}
                      />
                      {error && (
                        <p className="text-[11px] font-medium text-error-500 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Slug */}
              <div className="space-y-1.5">
                <label
                  htmlFor="slug"
                  className="text-xs font-bold text-neutral-600 dark:text-neutral-400 uppercase tracking-wider block"
                >
                  Slug
                </label>
                <Controller
                  control={control}
                  name="slug"
                  render={({ field }) => (
                    <input
                      {...field}
                      id="slug"
                      type="text"
                      readOnly
                      className="w-full px-3 py-2 bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-700 rounded-lg text-sm text-neutral-500 dark:text-neutral-400 font-mono cursor-not-allowed"
                    />
                  )}
                />
              </div>

              {/* Page Content */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-neutral-600 dark:text-neutral-400 uppercase tracking-wider block">
                  Page Content <span className="text-error-500">*</span>
                </label>
                <Controller
                  control={control}
                  name="content"
                  rules={{
                    validate: (value) => {
                      const tmp = document.createElement('div');
                      tmp.innerHTML = value;
                      const text = tmp.innerText.trim();
                      return text.length > 0 || 'Content is required.';
                    },
                  }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <>
                      <div
                        className={`rounded-lg overflow-hidden border ${
                          error
                            ? 'border-error-500'
                            : 'border-neutral-200 dark:border-neutral-800'
                        }`}
                      >
                        <TextEditor value={value} onChange={onChange} />
                      </div>
                      {error && (
                        <p className="text-[11px] font-medium text-error-500 mt-1">
                          {error.message}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-6">
              <button
                type="button"
                onClick={() => navigate('/configurations/static-pages')}
                className="px-5 py-2 text-sm font-bold text-neutral-600 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-white bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg transition-all hover:bg-neutral-50"
              >
                Discard
              </button>
              <button
                type="submit"
                disabled={isUpdating || !isDirty}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-all shadow-sm shadow-brand-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default StaticPageForm;
